import { z } from 'zod';
import { isPhoneNumber } from './utils/phone';

const phoneNumberSchema = z
  .string()
  .regex(/^[0-9]+$/)
  .optional();

// Schema for contact sub-fields (used in fallbackContact, without nested fallback)
// All fields are optional to support partial saves and full-sync logic
export const contactSubFieldsSchema = z.object({
  name: z.string().optional(),
  phone1: phoneNumberSchema.refine((value) => value === undefined || isPhoneNumber(value)),
  phone2: phoneNumberSchema.refine((value) => value === undefined || isPhoneNumber(value)),
  email: z.string().optional(),
});

// Schema for contact field values - matches ContactValue from backend
// All fields are optional to comply with full-sync logic
export const contactValueSchema = z.object({
  name: z.string().optional(),
  phone1: phoneNumberSchema.refine((value) => value === undefined || isPhoneNumber(value)),
  phone2: phoneNumberSchema.refine((value) => value === undefined || isPhoneNumber(value)),
  email: z.string().optional(),
  fallbackContact: contactSubFieldsSchema.optional(),
});

export type ContactValue = z.infer<typeof contactValueSchema>;
export type ContactSubFields = z.infer<typeof contactSubFieldsSchema>;

// Schema for a single position entry
// Contains id, label, coordinates, and optional status
// Note: label is optional to allow empty entries during editing
export const positionSchema = z.object({
  id: z.string(),
  label: z.string().optional().default(''),
  latitude: z.union([z.number(), z.string()]).optional(),
  longitude: z.union([z.number(), z.string()]).optional(),
  status: z.boolean().optional(),
});

export const coordinateValueSchema = z.object({
  latitude: z.union([z.number(), z.string()]).optional(),
  longitude: z.union([z.number(), z.string()]).optional(),
});

// Schema for positions field values - array of position entries
// Matches PositionsValue from backend
export const positionsValueSchema = z.array(positionSchema);

export type Position = z.infer<typeof positionSchema>;
export type PositionsValue = z.infer<typeof positionsValueSchema>;
export type CoordinateValue = z.infer<typeof coordinateValueSchema>;

// Schema for a single phone-number entry
// Contains id, name, and an optional phone number (digits only, "00" prefixed)
// Note: name is optional to allow empty entries during editing
export const phoneNumberEntrySchema = z.object({
  id: z.string(),
  name: z.string().optional().default(''),
  phone: z
    .string()
    .regex(/^[0-9]+$/)
    .optional(),
});

// Schema for phoneNumbers field values - array of phone-number entries
// Matches PhoneNumbersValue from backend
export const phoneNumbersValueSchema = z.array(phoneNumberEntrySchema);

export type PhoneNumberEntry = z.infer<typeof phoneNumberEntrySchema>;
export type PhoneNumbersValue = z.infer<typeof phoneNumbersValueSchema>;

// Schema for a single status range entry
// Contains time range and status configuration
export const statusRangeSchema = z.object({
  id: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  statusDuring: z.enum(['red', 'yellow', 'green']),
  statusDuringOptionId: z.string().optional(),
  fallbackAfter: z.enum(['red', 'yellow', 'green']).optional(),
  fallbackAfterOptionId: z.string().optional(),
});

// Schema for status field values - supports manual and scheduled modes
// Matches StatusValue from backend
export const statusValueSchema = z.object({
  mode: z.enum(['manual', 'scheduled']),
  manualStatus: z.enum(['red', 'yellow', 'green']).optional(),
  manualStatusOptionId: z.string().optional(),
  ranges: z.array(statusRangeSchema).optional(),
  defaultStatus: z.enum(['red', 'yellow', 'green']),
  defaultStatusOptionId: z.string().optional(),
});

// Schema for status option configuration
export const statusOptionSchema = z.object({
  id: z.string().optional(),
  color: z.enum(['red', 'yellow', 'green']),
  labelDe: z.string(),
  labelEn: z.string().optional(),
});

export type StatusColor = 'red' | 'yellow' | 'green';
export type StatusRange = z.infer<typeof statusRangeSchema>;
export type StatusValue = z.infer<typeof statusValueSchema>;
export type StatusOption = z.infer<typeof statusOptionSchema>;

const statusOptionsSchema = z.array(statusOptionSchema).min(2);

// Schema for metadata field properties - matches JsonSchemaType from backend
const metadataFieldPropertySchema = z
  .object({
    type: z.enum([
      'string',
      'number',
      'boolean',
      'date',
      'section',
      'contact',
      'coordinate',
      'positions',
      'phoneNumbers',
      'status',
      'relation-display',
    ]),
    format: z.literal('date-time').optional(),
    description: z.string(),
    description_en: z.string().optional(),
    minLength: z.number().optional(),
    enum: z.array(z.string()).min(1).optional(),
    permissions: z
      .record(
        z.string(),
        z.object({
          read: z.boolean(),
          update: z.boolean(),
        })
      )
      .optional(),
    customerAccess: z.enum(['none', 'read', 'read-and-write']).optional(),
    groupId: z.string().nullable().optional(),
    width: z.enum(['25', '33', '50', '100']).optional(),
    // String fields: render a textarea instead of a single-line input
    multiline: z.boolean().optional(),
    // Positions field status labels
    statusLabel: z.string().optional(),
    statusLabelEn: z.string().optional(),
    // Status field specific properties
    statusOptions: statusOptionsSchema.optional(),
    isGroupIndicator: z.boolean().optional(),
    icon: z.enum(['fuel', 'helideck', 'warning', 'check', 'lightning', 'medical']).optional(),
    relationDisplayVariant: z
      .enum(['ship-linked-windpark-helicopters', 'windpark-linked-ship-medics'])
      .optional(),
    sourceContactFieldKeys: z.array(z.string()).min(1).optional(),
    // Legacy support for old group field
    group: z.string().nullable().optional(),
  })
  .passthrough();

// Schema for metadata groups
const metadataGroupSchema = z
  .object({
    id: z.string(),
    labelDe: z.string(),
    labelEn: z.string().optional(),
    order: z.number(),
  })
  .passthrough();

// Full JsonSchemaType schema - matches backend types
export const jsonSchemaType = z
  .object({
    type: z.literal('object'),
    groups: z.array(metadataGroupSchema).optional(),
    properties: z.record(z.string(), metadataFieldPropertySchema),
    required: z.array(z.string()),
    propertyOrder: z.array(z.string()),
  })
  .passthrough();
